import PropTypes from 'prop-types'
import React, { Component, createRef } from 'react'
import { connect } from 'react-redux'
import { actions, track } from 'react-redux-form'
import AgreeTos from 'components/agree-tos'
import CandidateForm from 'interview/components/candidate-form'
import CandidateService from 'interview/services/candidate'
import WithdrawnThankYou from 'interview/components/withdrawn-thank-you'
import { mapLanguageToComponentProps } from 'interview/i18n'
import getCustomSupport from 'interview/selectors/custom-support'
import { newReloadOnNotFountError } from 'flags'
import * as analytics from 'analytics/performance'
import { H2, Subtitle } from 'components/styled/typography'
import { validators } from 'utils/form'
import { Confirmation } from 'components/styled/modal/confirmation'
import { Button } from 'components/styled/button'
import styled from 'styled-components'
import { Flex } from 'components/styled/containers/flex'
import {
  getCandidateInfoForAmplitude,
  getCompanyAndInterviewInfoForAmplitude,
} from 'selectors'

const { isRequired } = validators

const propTypes = {
  i18n: PropTypes.shape({
    interview: PropTypes.object.isRequired,
  }).isRequired,
  isPublic: PropTypes.bool,
  candidate: PropTypes.shape({
    email: PropTypes.string,
    uuid: PropTypes.string,
    allowWithdraw: PropTypes.bool,
    _id: PropTypes.string,
  }).isRequired,
  interview: PropTypes.shape({
    questions: PropTypes.array,
    formSetup: PropTypes.object,
    company: PropTypes.shape({
      _id: PropTypes.string,
      name: PropTypes.string,
      privacyLink: PropTypes.string,
    }),
    additionalFormFields: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        type: PropTypes.string,
        required: PropTypes.bool,
      })
    ).isRequired,
  }).isRequired,
  dispatch: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
  customSupport: PropTypes.shape({
    companyName: PropTypes.string,
    type: PropTypes.string,
    value: PropTypes.string,
  }),
  updateAdditionalFieldsValidityState: PropTypes.func,
  model: PropTypes.string,
}

const defaultProps = {
  disableEmail: false,
  isPublic: false,
  children: undefined,
  customSupport: undefined,
  updateAdditionalFieldsValidityState: undefined,
  model: 'candidate',
}

const mapStateToProps = (state, ownProps) => ({
  candidate:
    ownProps.model === 'previewCandidate'
      ? state.previewCandidate
      : state.candidate,
  interview:
    ownProps.model === 'previewCandidate'
      ? state.interview
      : state.interview.jobOpening,
  customSupport: getCustomSupport(state),
})

const AgreeTosContainer = styled.div`
  margin-bottom: ${({ theme }) => theme.space.s32};
`

export class InterviewFormWrapper extends Component {
  constructor(props) {
    super(props)

    this.state = {
      askWithdrawal: false,
      withdrawn: false,
      showAgreeTosError: false,
    }

    this.onChange = this.onChange.bind(this)
    this.preventEnterSubmit = this.preventEnterSubmit.bind(this)
    this.onClickDisabledForm = this.onClickDisabledForm.bind(this)
    this.onConfirmWithdraw = this.onConfirmWithdraw.bind(this)
    this.onClickNotInterestedAnymore =
      this.onClickNotInterestedAnymore.bind(this)
    this.agreeTosContainer = createRef(null)
  }

  componentWillUnmount() {
    if (this.timeout) {
      clearTimeout(this.timeout)
    }
  }

  onClickDisabledForm() {
    window.scroll(0, this.agreeTosContainer.current.offSetTop)
    this.setState({ showAgreeTosError: true })
  }

  onClickNotInterestedAnymore() {
    this.setState({ askWithdrawal: true })
  }

  onConfirmWithdraw() {
    const { candidate, interview } = this.props
    const companyAndInterviewInfo =
      getCompanyAndInterviewInfoForAmplitude(interview)
    const candidateInfo = getCandidateInfoForAmplitude(candidate)

    analytics.onClickEvent({
      locationId: 'interviewPage',
      id: 'confirmCandidateWithdrawButton',
      properties: {
        ...candidateInfo,
        ...companyAndInterviewInfo,
      },
    })

    this.setState({ askWithdrawal: false })
    return CandidateService.withdraw(candidate.uuid).then(() => {
      this.setState({ withdrawn: true })
    })
  }

  getFieldName(fieldName) {
    if (fieldName === 'phone') {
      return 'phoneNumber'
    }
    if (fieldName === 'link') {
      return 'links'
    }
    return fieldName
  }

  /**
   * We want to prevent accidental interview completion
   * and therefore prevent form submit with enter.
   * Shitty react-redux-form doesn't pass event to onSubmit handler and
   * therefore we have to add onKeyPress handler separately.
   */
  preventEnterSubmit(event) {
    if (event.key === 'Enter') {
      event.preventDefault()
    }
  }

  isAdditionalFieldRequired(value) {
    const { interview } = this.props
    const additionalField = interview.additionalFormFields.find(
      field => field._id === value.fieldId
    )
    return additionalField.fieldSetup.required
  }

  /**
   * Submit field values to backend when changed
   */

  reloadIfNotFoundError(err) {
    if (!newReloadOnNotFountError()) {
      throw err
    }
    if (err.response && err.response.status === 404) {
      // reload page if candidate not found: e.g. was deleted or interview ended
      window.location.reload()
    }
    throw err
  }

  updateFieldValue(fieldModel, value) {
    const {
      candidate,
      dispatch,
      interview: { formSetup },
    } = this.props
    const data = {
      uuid: candidate.uuid,
    }

    if (fieldModel === 'additionalFieldResponse') {
      this.updateAdditionalFieldResponse(fieldModel, value, data)
    } else {
      const [, fieldName] = fieldModel.split('.')
      data[fieldName] = value
      const field = this.getFieldName(fieldName)
      // Because we can't trust field status is updated when change was
      // dispatched above we have to validate manually here
      if (!(field in formSetup) || !formSetup[field].required || value) {
        const promise = CandidateService.update(data)
          // return true or react-redux-form will set field valid status to false
          .then(() => {
            return true
          })
          .catch(this.reloadIfNotFoundError)
        dispatch(actions.submit(fieldModel, promise))
      }
    }
  }

  updateAdditionalFieldResponse(fieldModel, value, data) {
    const { dispatch, model } = this.props

    data.additionalFieldResponse = value
    if (!this.isAdditionalFieldRequired(value) || value.answer) {
      const promise = CandidateService.update(data)
        // return true or react-redux-form will set field valid status to false
        .then(() => {
          return true
        })
        .catch(this.reloadIfNotFoundError)
      dispatch(actions.submit(`${model}.additionalFieldResponses`, promise))
    }
  }

  onChange(fieldModel, value) {
    const {
      dispatch,
      model,
      candidate: { additionalFieldResponses },
    } = this.props
    if (fieldModel === 'additionalFieldResponse') {
      if (additionalFieldResponses.find(f => f.fieldId === value.fieldId)) {
        dispatch(
          actions.change(
            track(`${model}.additionalFieldResponses`, {
              fieldId: value.fieldId,
            }),
            value
          )
        )
      } else {
        dispatch(actions.push(`${model}.additionalFieldResponses`, value))
      }
    } else {
      dispatch(actions.change(fieldModel, value))
    }
    if (model === 'candidate') {
      this.updateFieldValue(fieldModel, value)
    }
  }

  render() {
    const {
      i18n,
      isPublic,
      candidate,
      interview,
      children,
      onSubmit,
      customSupport,
      updateAdditionalFieldsValidityState,
      model,
    } = this.props
    const { askWithdrawal, withdrawn, showAgreeTosError } = this.state
    const { company } = interview

    const companyAndInterviewInfo =
      getCompanyAndInterviewInfoForAmplitude(interview)
    const candidateInfo = getCandidateInfoForAmplitude(candidate)

    if (withdrawn) {
      return <WithdrawnThankYou />
    }

    return (
      <div className="interview-form-wrapper">
        <H2 color="bodyText">{i18n.interview.title}</H2>
        <AgreeTosContainer ref={this.agreeTosContainer}>
          <AgreeTos
            model={`${model}.agreeTOS`}
            agreed={candidate.agreeTOS}
            validators={{ isRequired }}
            onChange={value => this.onChange(`${model}.agreeTOS`, value)}
            disabled={candidate.agreeTOS}
            privacyLink={company && company.privacyPolicy}
            customSupport={customSupport}
            showError={{ touched: !showAgreeTosError }}
          />
        </AgreeTosContainer>
        <div
          onClick={
            !candidate.agreeTOS && !isPublic
              ? this.onClickDisabledForm
              : undefined
          }
        >
          <CandidateForm
            onSubmit={onSubmit}
            onChange={this.onChange}
            model={model}
            updateAdditionalFieldsValidityState={
              updateAdditionalFieldsValidityState
                ? shouldSubmit =>
                    updateAdditionalFieldsValidityState(shouldSubmit)
                : undefined
            }
            preventEnterSubmit={this.preventEnterSubmit}
            disableEmail
          >
            {children}
          </CandidateForm>
          {candidate.allowWithdraw && (
            <Flex flexDirection="column" mt="s32" mb="s24">
              <Subtitle>{i18n.interview.withdrawLink}</Subtitle>
              <Button
                variant="secondary"
                onClick={
                  model === 'candidate'
                    ? () => this.onClickNotInterestedAnymore
                    : () => this.setState({ askWithdrawal: true })
                }
                text={i18n.withdraw.buttonText}
                icon="not_interested"
                event={
                  model === 'candidate' && {
                    locationId: 'interviewPage',
                    id: 'candidateWithdrawButton',
                    properties: {
                      ...candidateInfo,
                      ...companyAndInterviewInfo,
                    },
                  }
                }
              />
            </Flex>
          )}
          <Confirmation
            id="candidate-withdraw-modal"
            isOpen={askWithdrawal}
            onConfirm={
              model === 'candidate'
                ? () => this.onConfirmWithdraw()
                : () => this.setState({ askWithdrawal: false })
            }
            onCancel={() => this.setState({ askWithdrawal: false })}
            body={i18n.withdraw.confirm}
          />
        </div>
      </div>
    )
  }
}

InterviewFormWrapper.propTypes = propTypes
InterviewFormWrapper.defaultProps = defaultProps

export default connect(mapLanguageToComponentProps(mapStateToProps))(
  InterviewFormWrapper
)
